import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  useTheme,
  useMediaQuery
} from '@mui/material';
import { 
  CheckCircle as CheckCircleIcon,
  Download as DownloadIcon,
  Payment as PaymentIcon,
  Description as DescriptionIcon,
  Receipt as ReceiptIcon
} from '@mui/icons-material';
import { collectionGroup, query, where, getDocs, doc as docFn, getDoc, updateDoc, collection } from 'firebase/firestore';
import { db } from '../firebase/config';
import { functions } from '../firebase/config';
import { httpsCallable } from 'firebase/functions';
import SignatureCapture from '../components/estimates/SignatureCapture';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import { ensureHttps } from '../utils/urlUtils';

/**
 * CustomerPortal - Public portal for customers to view, sign, and pay for estimates
 * 
 * This is a token-based portal that doesn't require Firebase Auth.
 * Customers access it via /portal/:portalToken
 */
export default function CustomerPortal() {
  const { portalToken } = useParams();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [estimate, setEstimate] = useState(null);
  const [company, setCompany] = useState(null);
  const [job, setJob] = useState(null);
  const [signatureDialogOpen, setSignatureDialogOpen] = useState(false);
  const [signingLink, setSigningLink] = useState(null);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [paymentError, setPaymentError] = useState('');
  const [stripeConnected, setStripeConnected] = useState(false);

  useEffect(() => {
    const loadEstimate = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!portalToken) {
          setError('Invalid portal link. Please use the link provided in your email.');
          setLoading(false);
          return;
        }

        // Search for estimate by portalToken using collectionGroup
        let estimateData = null;
        let estimateDocRef = null;

        try {
          const estimatesQuery = query(
            collectionGroup(db, 'estimates'),
            where('portalToken', '==', portalToken)
          );
          
          const estimatesSnapshot = await getDocs(estimatesQuery);
          
          if (estimatesSnapshot.empty) {
            setError('This portal link is invalid or expired. Please contact the sender for a new link.');
            setLoading(false);
            return;
          }

          // Get the first matching estimate (should only be one)
          const estimateDoc = estimatesSnapshot.docs[0];
          estimateData = { id: estimateDoc.id, ...estimateDoc.data() };
          estimateDocRef = estimateDoc.ref;

          // Extract companyId from the document path
          // Path format: /companies/{companyId}/estimates/{estimateId}
          const pathParts = estimateDocRef.path.split('/');
          const companyId = pathParts[1];

          // Load company info for branding
          try {
            const companyRef = docFn(db, 'companies', companyId);
            const companyDoc = await getDoc(companyRef);
            if (companyDoc.exists()) {
              setCompany(companyDoc.data());
            }
          } catch (companyErr) {
            console.warn('Could not load company info:', companyErr);
            // Non-fatal error, continue without company info
          }

          // Build signing link if signingToken exists
          if (estimateData.signingToken) {
            const baseUrl = window.location.origin;
            setSigningLink(`${baseUrl}/sign-estimate/${estimateData.id}/${estimateData.signingToken}`);
          }

          estimateData._ref = estimateDocRef;
          setEstimate(estimateData);

          // If estimate is approved, try to load the associated job/invoice
          // Use the already extracted companyId from above
          if (estimateData.status === 'Approved' && estimateData.id && companyId) {
            try {
              const jobsRef = collection(db, 'companies', companyId, 'jobs');
              const jobsQuery = query(
                jobsRef,
                where('estimateId', '==', estimateData.id)
              );
              const jobsSnapshot = await getDocs(jobsQuery);
              
              if (!jobsSnapshot.empty) {
                // Get the first matching job (should only be one)
                const jobDoc = jobsSnapshot.docs[0];
                const jobData = { id: jobDoc.id, ...jobDoc.data() };
                setJob(jobData);
                console.log('[portal] Loaded job/invoice:', jobData.id);
              }
            } catch (jobErr) {
              console.warn('Could not load job/invoice:', jobErr);
              // Non-fatal error, continue without job
            }
          }

          // Check if company has Stripe connected (for showing Pay Online button)
          if (companyId) {
            try {
              const companyDocRef = docFn(db, 'companies', companyId);
              const companyDoc = await getDoc(companyDocRef);
              if (companyDoc.exists()) {
                const companyData = companyDoc.data();
                const stripeData = companyData.stripe || {};
                setStripeConnected(stripeData.connected || false);
              }
            } catch (stripeErr) {
              console.warn('Could not check Stripe connection:', stripeErr);
              // Non-fatal, continue without Stripe check
            }
          }

        } catch (searchError) {
          console.error('Error searching for estimate:', searchError);
          
          // Check if it's an index error
          if (searchError.code === 'failed-precondition' && searchError.message.includes('index')) {
            const indexLinkMatch = searchError.message.match(/https:\/\/console\.firebase\.google\.com[^\s)]+/);
            if (indexLinkMatch) {
              setError(`An index is required for the customer portal. Please create the index: ${indexLinkMatch[0]}`);
            } else {
              setError('An index is required for the customer portal. Please check the browser console for the index creation link.');
            }
            setLoading(false);
            return;
          }
          
          setError('Failed to load estimate. Please try again later.');
        }

      } catch (err) {
        console.error('Error loading portal data:', err);
        setError('Failed to load portal data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadEstimate();
  }, [portalToken]);

  const handleSignEstimate = () => {
    // Open signature dialog instead of redirecting
    setSignatureDialogOpen(true);
  };

  const handleSaveSignature = async (signatureData) => {
    if (!estimate || !estimate._ref) {
      alert('Unable to save signature. Please refresh the page.');
      return;
    }

    try {
      // Update the estimate with signature data
      await updateDoc(estimate._ref, {
        signatureUrl: signatureData.signatureUrl,
        signedAt: signatureData.signedAt,
        signedByName: signatureData.signedByName,
        status: 'Approved',
        updatedAt: new Date().toISOString()
      });

      // Update local estimate state
      const updatedEstimate = {
        ...estimate,
        signatureUrl: signatureData.signatureUrl,
        signedAt: signatureData.signedAt,
        signedByName: signatureData.signedByName,
        status: 'Approved'
      };
      setEstimate(updatedEstimate);
      setSignatureDialogOpen(false);

      // Reload the job/invoice after signing (in case it was auto-created)
      // Extract companyId from estimate path
      const estimateCompanyId = estimate._ref?.path.split('/')[1];
      if (estimateCompanyId && updatedEstimate.status === 'Approved' && updatedEstimate.id) {
        try {
          const jobsRef = collection(db, 'companies', estimateCompanyId, 'jobs');
          const jobsQuery = query(
            jobsRef,
            where('estimateId', '==', updatedEstimate.id)
          );
          const jobsSnapshot = await getDocs(jobsQuery);
          
          if (!jobsSnapshot.empty) {
            const jobDoc = jobsSnapshot.docs[0];
            const jobData = { id: jobDoc.id, ...jobDoc.data() };
            setJob(jobData);
            console.log('[portal] Loaded job/invoice after signing:', jobData.id);
          }
        } catch (jobErr) {
          console.warn('Could not reload job/invoice after signing:', jobErr);
        }
      }

      console.log('[portal] Signature saved successfully');
    } catch (error) {
      console.error('[portal] Error saving signature:', error);
      alert('Failed to save signature. Please try again.');
      throw error;
    }
  };

  // Handle Pay Online button click (for invoice/job)
  const handlePayOnline = async () => {
    if (!job || !estimate || !portalToken) {
      setPaymentError('Unable to process payment. Please refresh the page.');
      return;
    }

    setIsProcessingPayment(true);
    setPaymentError('');

    try {
      console.log('[portal] Creating portal checkout session for job:', job.id);
      
      // Call the PUBLIC portal checkout function (uses portalToken for validation)
      const createPortalCheckoutSession = httpsCallable(functions, 'createPortalCheckoutSession');
      const result = await createPortalCheckoutSession({
        portalToken: portalToken,
        jobId: job.id,
      });

      const { url } = result.data;

      if (!url) {
        throw new Error('No checkout URL returned from server');
      }

      console.log('[portal] Redirecting to Stripe Checkout:', url);
      
      // Redirect to Stripe Checkout
      window.location.href = url;
    } catch (error) {
      console.error('[portal] Error creating checkout session:', error);
      
      // Extract user-friendly error message
      let errorMessage = 'Failed to start payment process. Please try again.';
      if (error.message) {
        if (error.message.includes('not connected')) {
          errorMessage = 'Online payment is not available. Please contact the contractor directly.';
        } else if (error.message.includes('already paid')) {
          errorMessage = 'This invoice has already been paid.';
        } else {
          errorMessage = error.message;
        }
      }
      
      setPaymentError(errorMessage);
      setIsProcessingPayment(false);
    }
  };

  const handleDownloadInvoicePDF = async () => {
    if (!job || !estimate) return;

    try {
      // Preload images
      const preloadImage = (src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = ensureHttps(src);
        });
      };

      // Preload company logo if it exists
      if (company?.logo) {
        try {
          await preloadImage(company.logo);
        } catch (error) {
          console.warn('Failed to preload company logo:', error);
        }
      }

      // US Letter size: 612pt width, 792pt height
      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 24;
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      // Create a temporary div to render the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.setAttribute('data-pdf-content', 'true');
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.width = contentWidth + 'px';
      pdfContent.style.minHeight = contentHeight + 'px';
      pdfContent.style.padding = margin + 'px';
      pdfContent.style.boxSizing = 'border-box';
      pdfContent.style.background = '#fff';
      pdfContent.innerHTML = generateInvoicePDFHTML(job, estimate, company);
      document.body.appendChild(pdfContent);

      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate PDF using html2canvas and jsPDF
      const scale = 2;
      const canvas = await html2canvas(pdfContent, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: contentWidth,
        height: pdfContent.scrollHeight,
        windowWidth: contentWidth,
        windowHeight: pdfContent.scrollHeight,
        logging: false,
        imageTimeout: 15000
      });

      // Calculate scaling to fit letter size
      const actualWidth = canvas.width / scale;
      const actualHeight = canvas.height / scale;
      
      // Scale to fit content area in points (PDF uses points)
      const scaleX = contentWidth / actualWidth;
      const scaleY = contentHeight / actualHeight;
      const scaleFactor = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

      // Scale dimensions for PDF (in points)
      const scaledWidth = actualWidth * scaleFactor;
      const scaledHeight = actualHeight * scaleFactor;

      // Create PDF with letter size
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidth, pageHeight]
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // Center the image on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      pdf.save(`invoice-${job.jobId || job.id}.pdf`);

      // Cleanup
      document.body.removeChild(pdfContent);
    } catch (error) {
      console.error('Error generating invoice PDF:', error);
      alert('Failed to generate invoice PDF. Please try again.');
    }
  };

  const handleDownloadPDF = async () => {
    if (!estimate) return;

    try {
      // Preload images
      const preloadImage = (src) => {
        return new Promise((resolve, reject) => {
          const img = new Image();
          img.crossOrigin = 'anonymous';
          img.onload = () => resolve(img);
          img.onerror = reject;
          img.src = ensureHttps(src);
        });
      };

      // Preload signature image if it exists
      if (estimate.signatureUrl) {
        try {
          await preloadImage(estimate.signatureUrl);
        } catch (error) {
          console.warn('Failed to preload signature image:', error);
        }
      }

      // Preload company logo if it exists
      if (company?.logo) {
        try {
          await preloadImage(company.logo);
        } catch (error) {
          console.warn('Failed to preload company logo:', error);
        }
      }

      // US Letter size: 612pt width, 792pt height
      const pageWidth = 612;
      const pageHeight = 792;
      const margin = 24;
      const contentWidth = pageWidth - 2 * margin;
      const contentHeight = pageHeight - 2 * margin;

      // Create a temporary div to render the PDF content
      const pdfContent = document.createElement('div');
      pdfContent.setAttribute('data-pdf-content', 'true');
      pdfContent.style.position = 'absolute';
      pdfContent.style.left = '-9999px';
      pdfContent.style.width = contentWidth + 'px';
      pdfContent.style.minHeight = contentHeight + 'px';
      pdfContent.style.padding = margin + 'px';
      pdfContent.style.boxSizing = 'border-box';
      pdfContent.style.background = '#fff';
      pdfContent.innerHTML = generatePDFHTML(estimate, company);
      document.body.appendChild(pdfContent);

      // Wait a bit for images to load
      await new Promise(resolve => setTimeout(resolve, 500));

      // Generate PDF using html2canvas and jsPDF
      const scale = 2;
      const canvas = await html2canvas(pdfContent, {
        scale,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        width: contentWidth,
        height: pdfContent.scrollHeight,
        windowWidth: contentWidth,
        windowHeight: pdfContent.scrollHeight,
        logging: false,
        imageTimeout: 15000
      });

      // Calculate scaling to fit letter size
      const actualWidth = canvas.width / scale;
      const actualHeight = canvas.height / scale;
      
      // Scale to fit content area in points (PDF uses points)
      const scaleX = contentWidth / actualWidth;
      const scaleY = contentHeight / actualHeight;
      const scaleFactor = Math.min(scaleX, scaleY, 1); // Don't scale up, only down

      // Scale dimensions for PDF (in points)
      const scaledWidth = actualWidth * scaleFactor;
      const scaledHeight = actualHeight * scaleFactor;

      // Create PDF with letter size
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'pt',
        format: [pageWidth, pageHeight]
      });
      
      const imgData = canvas.toDataURL('image/jpeg', 1.0);
      
      // Center the image on the page
      const x = (pageWidth - scaledWidth) / 2;
      const y = margin;
      
      pdf.addImage(imgData, 'JPEG', x, y, scaledWidth, scaledHeight);
      pdf.save(`estimate-${estimate.estimateId || estimate.id}.pdf`);

      // Cleanup
      document.body.removeChild(pdfContent);
    } catch (error) {
      console.error('Error generating PDF:', error);
      alert('Failed to generate PDF. Please try again.');
    }
  };

  const generatePDFHTML = (estimate, company) => {
    const primaryColor = '#1976d2';
    const estimateDate = new Date(estimate.createdAt).toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
    const dueDate = estimate.dueDate 
      ? new Date(estimate.dueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      : new Date(new Date(estimate.createdAt).getTime() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    
    // Calculate subtotal and tax
    const subtotal = estimate.lineItems?.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0) || estimate.total || 0;
    const taxRate = estimate.taxRate || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    // Letter size: 612pt width, 792pt height
    // With 24pt margins: 564pt content width, 744pt content height
    const margin = 24;
    const contentWidth = 612 - (2 * margin);
    const contentHeight = 792 - (2 * margin);

    const customerAddress = estimate.contactInfo?.streetAddress 
      ? `${estimate.contactInfo.streetAddress}${estimate.contactInfo.city ? ', ' + estimate.contactInfo.city : ''}${estimate.contactInfo.zipCode ? ' ' + estimate.contactInfo.zipCode : ''}`
      : '';

    return `
      <div style="width: ${contentWidth}px; min-height: ${contentHeight}px; padding: ${margin}px; font-family: Arial, sans-serif; color: #222; background: #fff; box-sizing: border-box;">
        <!-- Header Section -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px;">
          <!-- Left: Company Info -->
          <div style="flex: 1;">
            <h1 style="margin: 0 0 8px 0; font-size: 20px; font-weight: bold; color: #222; text-transform: uppercase;">
              ${(company?.name || 'YOUR COMPANY INC.').toUpperCase()}
            </h1>
            <div style="font-size: 12px; color: #555; line-height: 1.6;">
              ${company?.address ? `<div>${company.address}</div>` : ''}
              ${company?.phone ? `<div>${company.phone}</div>` : ''}
              ${company?.email ? `<div>${company.email}</div>` : ''}
              ${company?.website ? `<div>${company.website}</div>` : ''}
              ${company?.licenseNumber ? `<div>License #: ${company.licenseNumber}</div>` : ''}
            </div>
          </div>
          
          <!-- Right: Logo and Title -->
          <div style="text-align: right; flex: 1;">
            ${company?.logo ? `
              <div style="margin-left: auto; margin-bottom: 12px; display: flex; justify-content: flex-end;">
                <img 
                  src="${ensureHttps(company.logo)}"
                  alt="Company logo"
                  style="max-width: 120px; max-height: 60px; object-fit: contain;"
                  crossOrigin="anonymous"
                />
              </div>
            ` : `
              <div style="width: 120px; height: 60px; border: 1px dashed #ccc; border-radius: 4px; margin-left: auto; margin-bottom: 12px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 10px; color: #999;">
                <div style="font-size: 20px; margin-bottom: 4px;">☁</div>
                <div>Upload Logo</div>
              </div>
            `}
            
            <!-- ESTIMATE PROPOSAL Title -->
            <div style="margin-bottom: 16px;">
              <div style="font-size: 24px; font-weight: bold; color: ${primaryColor}; line-height: 1.2; letter-spacing: 1px;">
                ESTIMATE
              </div>
              <div style="font-size: 24px; font-weight: bold; color: ${primaryColor}; line-height: 1.2; letter-spacing: 1px;">
                PROPOSAL
              </div>
            </div>
            
            <!-- Estimate Details -->
            <div style="font-size: 11px; text-align: right; line-height: 1.8;">
              <div>
                <span style="color: ${primaryColor}; font-weight: bold;">Estimate #</span>
                <span style="margin-left: 8px; color: #222;">${estimate.estimateId || estimate.id}</span>
              </div>
              <div>
                <span style="color: ${primaryColor}; font-weight: bold;">Estimate date</span>
                <span style="margin-left: 8px; color: #222;">${estimateDate}</span>
              </div>
              <div>
                <span style="color: ${primaryColor}; font-weight: bold;">Due date</span>
                <span style="margin-left: 8px; color: #222;">${dueDate}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bill To Section -->
        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold; color: ${primaryColor};">
            Bill To:
          </h3>
          <div style="font-size: 12px; color: #222; line-height: 1.6;">
            <div style="font-weight: bold;">${estimate.customerName}</div>
            ${customerAddress ? `<div>${customerAddress}</div>` : ''}
            ${estimate.contactInfo?.phone ? `<div>Phone: ${estimate.contactInfo.phone}</div>` : ''}
            ${estimate.contactInfo?.email ? `<div>Email: ${estimate.contactInfo.email}</div>` : ''}
          </div>
        </div>

        ${estimate.description ? `
          <div style="margin-bottom: 16px; font-size: 12px; color: #222;">
            <strong>Description:</strong> ${estimate.description}
          </div>
        ` : ''}

        <!-- Line Items Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
          <thead>
            <tr style="background: ${primaryColor};">
              <th style="padding: 10px 8px; text-align: left; color: #fff; font-size: 11px; font-weight: bold;">QTY</th>
              <th style="padding: 10px 8px; text-align: left; color: #fff; font-size: 11px; font-weight: bold;">Description</th>
              <th style="padding: 10px 8px; text-align: right; color: #fff; font-size: 11px; font-weight: bold;">Unit Price</th>
              <th style="padding: 10px 8px; text-align: right; color: #fff; font-size: 11px; font-weight: bold;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${(estimate.lineItems || []).map(item => `
              <tr style="border-bottom: 1px solid #eee;">
                <td style="padding: 10px 8px; font-size: 11px; text-align: left;">${(item.quantity || 0).toFixed(2)}</td>
                <td style="padding: 10px 8px; font-size: 11px; text-align: left;">${item.description || item.name || ''}</td>
                <td style="padding: 10px 8px; font-size: 11px; text-align: right;">$${(item.unitPrice || 0).toFixed(2)}</td>
                <td style="padding: 10px 8px; font-size: 11px; text-align: right;">$${((item.quantity || 0) * (item.unitPrice || 0)).toFixed(2)}</td>
              </tr>
            `).join('')}
          </tbody>
        </table>

        <!-- Summary Section -->
        <div style="display: flex; justify-content: flex-end; margin-bottom: 24px;">
          <div style="width: 200px; font-size: 11px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span>Subtotal</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            ${taxRate > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                <span>Sales Tax (${taxRate}%)</span>
                <span>$${taxAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div style="border-top: 1px solid #ddd; margin-top: 8px; padding-top: 8px; display: flex; justify-content: space-between;">
              <span style="color: ${primaryColor}; font-weight: bold;">Total (USD)</span>
              <span style="color: ${primaryColor}; font-weight: bold;">$${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <!-- Footer Section -->
        <div style="display: flex; justify-content: space-between; margin-top: auto; padding-top: 20px;">
          <!-- Terms and Conditions -->
          <div style="flex: 1; font-size: 10px; color: #555;">
            <h4 style="margin: 0 0 8px 0; font-size: 11px; font-weight: bold; color: ${primaryColor};">
              Terms and Conditions
            </h4>
            <div style="line-height: 1.6; white-space: pre-line;">
              ${estimate.companyInfo?.settings?.defaultTerms || company?.settings?.defaultTerms || company?.defaultTerms || `Payment is due in 14 days.\nPlease make checks payable to: ${company?.name || estimate.companyInfo?.name || 'Your Company Inc.'}`}
            </div>
          </div>

          <!-- Customer Signature -->
          <div style="flex: 1; text-align: center; padding-top: 40px;">
            ${estimate.signatureUrl ? `
              <img
                src="${ensureHttps(estimate.signatureUrl)}"
                alt="Customer signature"
                style="max-width: 200px; max-height: 80px; border: 1px solid #ddd; padding: 4px; margin: 0 auto 8px auto; display: block;"
                crossOrigin="anonymous"
              />
              <div style="font-size: 10px; color: ${primaryColor}; font-weight: bold; margin-bottom: 4px;">
                Customer Signature
              </div>
              ${estimate.signedByName ? `
                <div style="font-size: 9px; color: #555; margin-bottom: 2px;">
                  ${estimate.signedByName}
                </div>
              ` : ''}
              ${estimate.signedAt ? `
                <div style="font-size: 9px; color: #555;">
                  ${new Date(estimate.signedAt).toLocaleDateString('en-US', {
                    month: '2-digit',
                    day: '2-digit',
                    year: 'numeric'
                  })}
                </div>
              ` : ''}
            ` : `
              <div style="border-top: 1px solid #ddd; width: 200px; margin: 0 auto 8px auto; padding-top: 8px;"></div>
              <div style="font-size: 10px; color: ${primaryColor}; font-weight: bold;">
                Customer Signature
              </div>
            `}
          </div>
        </div>
      </div>
    `;
  };

  const generateInvoicePDFHTML = (job, estimate, company) => {
    const primaryColor = '#1976d2';
    const invoiceDate = new Date(job.createdAt || new Date()).toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
    const dueDate = job.dueDate 
      ? new Date(job.dueDate).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' })
      : new Date(new Date(job.createdAt || new Date()).getTime() + 14 * 24 * 60 * 60 * 1000).toLocaleDateString('en-US', { month: '2-digit', day: '2-digit', year: 'numeric' });
    
    // Calculate subtotal and tax from job workItems or estimate lineItems
    const lineItems = (job.workItems || []).length > 0 ? job.workItems : (estimate?.lineItems || []);
    const subtotal = lineItems.reduce((sum, item) => {
      if (item.unitPrice !== undefined && item.quantity !== undefined) {
        return sum + (item.unitPrice * item.quantity);
      }
      return sum + (item.total || 0);
    }, 0);
    const taxRate = estimate?.taxRate || 0;
    const taxAmount = subtotal * (taxRate / 100);
    const total = subtotal + taxAmount;

    // Letter size: 612pt width, 792pt height
    const margin = 24;
    const contentWidth = 612 - (2 * margin);
    const contentHeight = 792 - (2 * margin);

    const customerAddress = estimate?.contactInfo?.streetAddress 
      ? `${estimate.contactInfo.streetAddress}${estimate.contactInfo.city ? ', ' + estimate.contactInfo.city : ''}${estimate.contactInfo.zipCode ? ' ' + estimate.contactInfo.zipCode : ''}`
      : (job.address || '');

    return `
      <div style="width: ${contentWidth}px; min-height: ${contentHeight}px; padding: ${margin}px; font-family: Arial, sans-serif; color: #222; background: #fff; box-sizing: border-box;">
        <!-- Header Section -->
        <div style="display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px;">
          <!-- Left: Company Info -->
          <div style="flex: 1;">
            <h1 style="margin: 0 0 8px 0; font-size: 20px; font-weight: bold; color: #222; text-transform: uppercase;">
              ${(company?.name || 'YOUR COMPANY INC.').toUpperCase()}
            </h1>
            <div style="font-size: 12px; color: #555; line-height: 1.6;">
              ${company?.address ? `<div>${company.address}</div>` : ''}
              ${company?.phone ? `<div>${company.phone}</div>` : ''}
              ${company?.email ? `<div>${company.email}</div>` : ''}
              ${company?.website ? `<div>${company.website}</div>` : ''}
              ${company?.licenseNumber ? `<div>License #: ${company.licenseNumber}</div>` : ''}
            </div>
          </div>
          
          <!-- Right: Logo and Title -->
          <div style="text-align: right; flex: 1;">
            ${company?.logo ? `
              <div style="margin-left: auto; margin-bottom: 12px; display: flex; justify-content: flex-end;">
                <img 
                  src="${ensureHttps(company.logo)}"
                  alt="Company logo"
                  style="max-width: 120px; max-height: 60px; object-fit: contain;"
                  crossOrigin="anonymous"
                />
              </div>
            ` : `
              <div style="width: 120px; height: 60px; border: 1px dashed #ccc; border-radius: 4px; margin-left: auto; margin-bottom: 12px; display: flex; flex-direction: column; align-items: center; justify-content: center; font-size: 10px; color: #999;">
                <div style="font-size: 20px; margin-bottom: 4px;">☁</div>
                <div>Upload Logo</div>
              </div>
            `}
            
            <!-- INVOICE Title -->
            <div style="margin-bottom: 16px;">
              <div style="font-size: 24px; font-weight: bold; color: ${primaryColor}; line-height: 1.2; letter-spacing: 1px;">
                INVOICE
              </div>
            </div>
            
            <!-- Invoice Details -->
            <div style="font-size: 11px; text-align: right; line-height: 1.8;">
              <div>
                <span style="color: ${primaryColor}; font-weight: bold;">Invoice #</span>
                <span style="margin-left: 8px; color: #222;">INV-${job.jobId || job.id}</span>
              </div>
              <div>
                <span style="color: ${primaryColor}; font-weight: bold;">Invoice date</span>
                <span style="margin-left: 8px; color: #222;">${invoiceDate}</span>
              </div>
              <div>
                <span style="color: ${primaryColor}; font-weight: bold;">Due date</span>
                <span style="margin-left: 8px; color: #222;">${dueDate}</span>
              </div>
            </div>
          </div>
        </div>

        <!-- Bill To Section -->
        <div style="margin-bottom: 20px;">
          <h3 style="margin: 0 0 8px 0; font-size: 14px; font-weight: bold; color: ${primaryColor};">
            Bill To:
          </h3>
          <div style="font-size: 12px; color: #222; line-height: 1.6;">
            <div style="font-weight: bold;">${job.customerName || estimate?.customerName}</div>
            ${customerAddress ? `<div>${customerAddress}</div>` : ''}
            ${estimate?.contactInfo?.phone ? `<div>Phone: ${estimate.contactInfo.phone}</div>` : ''}
            ${estimate?.contactInfo?.email ? `<div>Email: ${estimate.contactInfo.email}</div>` : ''}
          </div>
        </div>

        ${job.description || estimate?.description ? `
          <div style="margin-bottom: 16px; font-size: 12px; color: #222;">
            <strong>Description:</strong> ${job.description || estimate?.description}
          </div>
        ` : ''}

        <!-- Line Items Table -->
        <table style="width: 100%; border-collapse: collapse; margin-bottom: 16px;">
          <thead>
            <tr style="background: ${primaryColor};">
              <th style="padding: 10px 8px; text-align: left; color: #fff; font-size: 11px; font-weight: bold;">QTY</th>
              <th style="padding: 10px 8px; text-align: left; color: #fff; font-size: 11px; font-weight: bold;">Description</th>
              <th style="padding: 10px 8px; text-align: right; color: #fff; font-size: 11px; font-weight: bold;">Unit Price</th>
              <th style="padding: 10px 8px; text-align: right; color: #fff; font-size: 11px; font-weight: bold;">Amount</th>
            </tr>
          </thead>
          <tbody>
            ${lineItems.map((item, idx) => {
              const qty = item.quantity || 1;
              const unitPrice = item.unitPrice || 0;
              const amount = item.total || (qty * unitPrice);
              return `
                <tr style="border-bottom: 1px solid #eee;">
                  <td style="padding: 10px 8px; font-size: 11px; text-align: left;">${qty.toFixed(2)}</td>
                  <td style="padding: 10px 8px; font-size: 11px; text-align: left;">${item.description || item.name || 'Item'}</td>
                  <td style="padding: 10px 8px; font-size: 11px; text-align: right;">$${unitPrice.toFixed(2)}</td>
                  <td style="padding: 10px 8px; font-size: 11px; text-align: right;">$${amount.toFixed(2)}</td>
                </tr>
              `;
            }).join('')}
          </tbody>
        </table>

        <!-- Summary Section -->
        <div style="display: flex; justify-content: flex-end; margin-bottom: 24px;">
          <div style="width: 200px; font-size: 11px;">
            <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
              <span>Subtotal</span>
              <span>$${subtotal.toFixed(2)}</span>
            </div>
            ${taxRate > 0 ? `
              <div style="display: flex; justify-content: space-between; margin-bottom: 6px;">
                <span>Sales Tax (${taxRate}%)</span>
                <span>$${taxAmount.toFixed(2)}</span>
              </div>
            ` : ''}
            <div style="border-top: 1px solid #ddd; margin-top: 8px; padding-top: 8px; display: flex; justify-content: space-between;">
              <span style="color: ${primaryColor}; font-weight: bold;">Total (USD)</span>
              <span style="color: ${primaryColor}; font-weight: bold;">$${total.toFixed(2)}</span>
            </div>
          </div>
        </div>

        <!-- Footer Section -->
        <div style="margin-top: auto; padding-top: 20px;">
          <div style="font-size: 10px; color: #555;">
            <h4 style="margin: 0 0 8px 0; font-size: 11px; font-weight: bold; color: ${primaryColor};">
              Terms and Conditions
            </h4>
            <div style="line-height: 1.6; white-space: pre-line;">
              ${company?.settings?.defaultTerms || company?.defaultTerms || `Payment is due in 14 days.\nPlease make checks payable to: ${company?.name || 'Your Company Inc.'}`}
            </div>
          </div>
        </div>
      </div>
    `;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '$0.00';
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount);
  };

  if (loading) {
    return (
      <Container 
        maxWidth="lg" 
        sx={{ 
          py: { xs: 4, sm: 6, md: 8 }, 
          px: { xs: 2, sm: 3 },
          display: 'flex', 
          justifyContent: 'center' 
        }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error) {
    const indexLinkMatch = error.match(/https:\/\/console\.firebase\.google\.com[^\s)]+/);
    return (
      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 4, sm: 6, md: 8 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
        {indexLinkMatch && (
          <Alert severity="info">
            <Button
              variant="contained"
              color="primary"
              href={indexLinkMatch[0]}
              target="_blank"
              rel="noopener noreferrer"
              fullWidth={isMobile}
            >
              Create Index
            </Button>
          </Alert>
        )}
      </Container>
    );
  }

  if (!estimate) {
    return (
      <Container 
        maxWidth="md" 
        sx={{ 
          py: { xs: 4, sm: 6, md: 8 },
          px: { xs: 2, sm: 3 }
        }}
      >
        <Alert severity="warning">Estimate not found.</Alert>
      </Container>
    );
  }

  const subtotal = estimate.lineItems?.reduce((sum, item) => sum + (item.quantity * item.unitPrice), 0) || estimate.total || 0;
  const taxRate = estimate.taxRate || 0;
  const taxAmount = subtotal * (taxRate / 100);
  const total = subtotal + taxAmount;
  const isSigned = estimate.status === 'Approved' && estimate.signatureUrl;

  return (
    <Container 
      maxWidth="lg" 
      sx={{ 
        py: { xs: 2, sm: 3, md: 4 },
        px: { xs: 1, sm: 2, md: 3 }
      }}
    >
      <Paper 
        elevation={3} 
        sx={{ 
          p: { xs: 2, sm: 3, md: 4 },
          mx: { xs: 0, sm: 1 }
        }}
      >
        {/* Header */}
        <Box sx={{ mb: { xs: 3, sm: 4 }, textAlign: 'center' }}>
          {company?.logo && (
            <Box sx={{ mb: 2 }}>
              <img
                src={ensureHttps(company.logo)}
                alt={company.name || 'Company'}
                style={{ 
                  maxHeight: isMobile ? 40 : 60, 
                  maxWidth: isMobile ? 150 : 200,
                  width: 'auto',
                  height: 'auto'
                }}
              />
            </Box>
          )}
          <Typography 
            variant={isMobile ? 'h5' : 'h4'} 
            gutterBottom
            sx={{ fontSize: { xs: '1.5rem', sm: '2rem', md: '2.125rem' } }}
          >
            {company?.name || 'Your Estimate'}
          </Typography>
          <Typography 
            variant={isMobile ? 'h6' : 'h5'} 
            color="primary" 
            gutterBottom
            sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
          >
            Estimate #{estimate.estimateId || estimate.id}
          </Typography>
          <Typography 
            variant="body2" 
            color="text.secondary"
            sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
          >
            {formatDate(estimate.createdAt)}
          </Typography>
        </Box>

        <Divider sx={{ mb: { xs: 3, sm: 4 } }} />

        {/* Status Banner */}
        {isSigned && (
          <Alert 
            severity="success" 
            icon={<CheckCircleIcon />} 
            sx={{ mb: { xs: 3, sm: 4 } }}
          >
            This estimate has been approved and signed.
            {estimate.signedAt && (
              <Typography 
                variant="caption" 
                display="block" 
                sx={{ mt: 1, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                Signed on {formatDate(estimate.signedAt)} by {estimate.signedByName || 'Customer'}
              </Typography>
            )}
          </Alert>
        )}

        {/* Customer Info */}
        <Card sx={{ mb: { xs: 3, sm: 4 } }}>
          <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
            <Typography 
              variant={isMobile ? 'subtitle1' : 'h6'} 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Bill To:
            </Typography>
            <Typography 
              variant="body1" 
              fontWeight="bold"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {estimate.customerName}
            </Typography>
            {estimate.contactInfo?.streetAddress && (
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {estimate.contactInfo.streetAddress}
              </Typography>
            )}
            {estimate.contactInfo?.city && estimate.contactInfo?.zipCode && (
              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
              >
                {estimate.contactInfo.city}, {estimate.contactInfo.zipCode}
              </Typography>
            )}
          </CardContent>
        </Card>

        {/* Description */}
        {estimate.description && (
          <Box sx={{ mb: { xs: 3, sm: 4 } }}>
            <Typography 
              variant={isMobile ? 'subtitle1' : 'h6'} 
              gutterBottom
              sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
            >
              Description
            </Typography>
            <Typography 
              variant="body1"
              sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
            >
              {estimate.description}
            </Typography>
          </Box>
        )}

        {/* Line Items */}
        <Box sx={{ mb: { xs: 3, sm: 4 } }}>
          <Typography 
            variant={isMobile ? 'subtitle1' : 'h6'} 
            gutterBottom
            sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
          >
            Items
          </Typography>
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <TableContainer component={Paper} variant="outlined">
              <Table size={isMobile ? 'small' : 'medium'}>
                <TableHead>
                  <TableRow>
                    <TableCell 
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Description
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Qty
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      {isMobile ? 'Price' : 'Unit Price'}
                    </TableCell>
                    <TableCell 
                      align="right"
                      sx={{ 
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        fontWeight: 'bold',
                        whiteSpace: 'nowrap'
                      }}
                    >
                      Total
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(estimate.lineItems || []).map((item, index) => (
                    <TableRow key={index}>
                      <TableCell
                        sx={{ 
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          maxWidth: { xs: '150px', sm: 'none' }
                        }}
                      >
                        {item.description || item.name || 'Item'}
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {item.quantity || 0}
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {formatCurrency(item.unitPrice || 0)}
                      </TableCell>
                      <TableCell 
                        align="right"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        {formatCurrency((item.quantity || 0) * (item.unitPrice || 0))}
                      </TableCell>
                    </TableRow>
                  ))}
                  <TableRow>
                    <TableCell 
                      colSpan={3} 
                      align="right"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        Subtotal:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant="body1" 
                        fontWeight="bold"
                        sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                      >
                        {formatCurrency(subtotal)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                  {taxRate > 0 && (
                    <TableRow>
                      <TableCell 
                        colSpan={3} 
                        align="right"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        <Typography 
                          variant="body1"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          Tax ({taxRate}%):
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography 
                          variant="body1"
                          sx={{ fontSize: { xs: '0.875rem', sm: '1rem' } }}
                        >
                          {formatCurrency(taxAmount)}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                  <TableRow>
                    <TableCell 
                      colSpan={3} 
                      align="right"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      <Typography 
                        variant={isMobile ? 'body1' : 'h6'}
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        Total:
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography 
                        variant={isMobile ? 'body1' : 'h6'}
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}
                      >
                        {formatCurrency(total)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        </Box>

        <Divider sx={{ my: { xs: 3, sm: 4 } }} />

        {/* Invoice Section - Show when job exists and estimate is approved/signed */}
        {job && (estimate.status === 'Approved' || isSigned) && (
          <>
            <Box sx={{ mb: { xs: 3, sm: 4 } }}>
              <Typography 
                variant={isMobile ? 'h6' : 'h5'} 
                gutterBottom
                sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem', md: '1.75rem' } }}
              >
                Invoice
              </Typography>
              <Card sx={{ mb: { xs: 2, sm: 3 } }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, flexWrap: 'wrap', gap: 2 }}>
                    <Box>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Invoice # {job.jobId || job.id}
                      </Typography>
                      <Typography 
                        variant="body2" 
                        color="text.secondary"
                        sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                      >
                        Status: {job.status || 'N/A'}
                      </Typography>
                    </Box>
                    <Box sx={{ textAlign: { xs: 'left', sm: 'right' } }}>
                      <Chip
                        label={
                          job.paymentStatus === 'paid' 
                            ? 'Paid' 
                            : job.paymentStatus === 'Partially Paid' || job.paymentStatus === 'pending'
                            ? 'Pending'
                            : 'Unpaid'
                        }
                        color={
                          job.paymentStatus === 'paid' 
                            ? 'success' 
                            : job.paymentStatus === 'Partially Paid' || job.paymentStatus === 'pending'
                            ? 'warning'
                            : 'error'
                        }
                        size={isMobile ? 'small' : 'medium'}
                        sx={{ mb: 1 }}
                      />
                      <Typography 
                        variant={isMobile ? 'h6' : 'h5'}
                        sx={{ fontSize: { xs: '1rem', sm: '1.25rem', md: '1.5rem' } }}
                      >
                        {formatCurrency(job.total || 0)}
                      </Typography>
                      {job.paymentStatus !== 'paid' && job.remainingBalance !== undefined && (
                        <Typography 
                          variant="body2" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                        >
                          Balance: {formatCurrency(job.remainingBalance || job.total || 0)}
                        </Typography>
                      )}
                      {job.paidAt && (
                        <Typography 
                          variant="caption" 
                          color="text.secondary"
                          sx={{ fontSize: { xs: '0.7rem', sm: '0.75rem' } }}
                        >
                          Paid on {formatDate(job.paidAt)}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  {job.description && (
                    <Typography 
                      variant="body2"
                      sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                    >
                      {job.description}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            </Box>
            
            {paymentError && (
              <Alert severity="error" sx={{ mb: { xs: 2, sm: 3 } }}>
                {paymentError}
              </Alert>
            )}
          </>
        )}

        <Divider sx={{ my: { xs: 3, sm: 4 } }} />

        {/* Actions */}
        <Box 
          sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            gap: 2, 
            justifyContent: 'center', 
            alignItems: 'stretch',
            '& > button': {
              width: { xs: '100%', sm: 'auto' },
              minWidth: { xs: '100%', sm: '200px' }
            }
          }}
        >
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={handleDownloadPDF}
            size={isMobile ? 'medium' : 'large'}
            fullWidth={isMobile}
          >
            Download PDF
          </Button>

          {!isSigned && estimate.signingToken && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<CheckCircleIcon />}
              onClick={handleSignEstimate}
              size={isMobile ? 'medium' : 'large'}
              fullWidth={isMobile}
            >
              {isMobile ? 'Sign Estimate' : 'Approve & Sign Estimate'}
            </Button>
          )}

          {/* Invoice Actions - Show when job exists and estimate is approved/signed */}
          {job && (estimate.status === 'Approved' || isSigned) && (
            <>
              <Button
                variant="outlined"
                startIcon={<ReceiptIcon />}
                onClick={handleDownloadInvoicePDF}
                size={isMobile ? 'medium' : 'large'}
                fullWidth={isMobile}
              >
                Download Invoice PDF
              </Button>

              {/* Pay Online button - show when invoice is not fully paid AND Stripe is connected */}
              {job.paymentStatus !== 'paid' && 
               job.paymentStatus !== 'Paid' && 
               stripeConnected && (
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<PaymentIcon />}
                  onClick={handlePayOnline}
                  disabled={isProcessingPayment}
                  size={isMobile ? 'medium' : 'large'}
                  fullWidth={isMobile}
                >
                  {isProcessingPayment ? 'Processing...' : 'Pay Online'}
                </Button>
              )}
              
              {/* Message when Stripe is not connected */}
              {job.paymentStatus !== 'paid' && 
               job.paymentStatus !== 'Paid' && 
               !stripeConnected && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Online payment is not available. Please contact the contractor directly to pay.
                </Typography>
              )}
            </>
          )}
        </Box>

        {isSigned && (
          <Box sx={{ mt: { xs: 2, sm: 3 }, textAlign: 'center' }}>
            <Chip
              icon={<CheckCircleIcon />}
              label="Approved"
              color="success"
              size={isMobile ? 'medium' : 'large'}
            />
          </Box>
        )}

        {/* Signature Capture Dialog */}
        <SignatureCapture
          open={signatureDialogOpen}
          onClose={() => setSignatureDialogOpen(false)}
          onSave={handleSaveSignature}
          estimateId={estimate?.id}
          existingSignature={estimate?.signatureUrl}
        />
      </Paper>
    </Container>
  );
}
